<template>
    <nav>
        <ul class="pagination">
            <li v-if="hasFirstLastButtons"
                :class="['page-item', firstPageSelected() ? 'disabled' : '']">
                <a class="page-link"
                    @click="selectFirstPage()">
                    First
                </a>
            </li>
            <li v-if="!(firstPageSelected())"
                :class="['page-item', firstPageSelected() ? 'disabled' : '']">
                <a @click="prevPage()" class="page-link">Previous</a>
            </li>
            <li v-for="page in pages"
                :key="page.idx"
                :class="['page-item', page.selected ? 'active' : '', page.disabled ? 'disabled' : '', page.pageBreak ? 'page-break': '']">
                <a v-if="page.pageBreak" class="page-link">...</a>
                <a v-else-if="page.disabled" class="page-link">{{ page.content }}</a>
                <a v-else class="page-link" @click="handlePageSelected(page.idx + 1)">{{ page.content }}</a>
            </li>
            <li v-if="!(lastPageSelected())"
                :class="['page-item', lastPageSelected() ? 'disabled' : '']">
                <a @click="nextPage()" class="page-link">Next</a>
            </li>
            <li v-if="hasFirstLastButtons"
                :class="['page-item', lastPageSelected() ? 'disabled' : '']">
                <a class="page-link"
                    @click="selectLastPage()">
                    Last
                </a>
            </li>
        </ul>
    </nav>
</template>

<script>
export default {
    name: 'mead-pagination',
    props: {
        /**
         * The currently selected page number (1-indexed). Supports v-model binding
         * via the internalValue/selected computed and the 'input' event.
         */
        value: {type: Number, default: 1},
        /**
         * Total number of pages to paginate across.
         */
        pageCount: { type: Number, required: true },
        /**
         * Number of visible page links shown around (and including) the current page
         * before truncation kicks in.
         */
        pageRange: {type: Number, default: 3},
        /**
         * Number of fixed page links shown at the very start and very end of the
         * pagination list when the page range is truncated with a page break.
         */
        limitPages: {type: Number, default: 3},
        /**
         * Whether to show explicit "First" and "Last" page navigation buttons.
         */
        hasFirstLastButtons: {type: Boolean, default: false},
        /**
         * Whether to hide the "Previous" and "Next" page navigation links.
         */
        hidePrevNext: {type: Boolean, default: false},
    },
    data() {
        return {
            internalValue: this.value
        }
    },
    computed: {
        selected: {
            get() { return this.internalValue },
            set(newValue) { this.internalValue = newValue}
        },
        pages() {
            // Dict with pages
            const pages = {};

            // If all pages fit in the range
            if(this.pageCount <= this.pageRange) {
                for (let i = 0; i < this.pageCount; i++) {
                    const page = {
                        idx: i,
                        content: i + 1,
                        selected: i === (this.selected - 1)
                    }
                    pages[i] = page;
                }
            } else {
                // If they do not fit in the range:
                // Split the range
                const halvedRange = Math.floor(this.pageRange / 2);

                // Small util to set a page
                const setPage = idx => {
                    const page = {
                        idx,
                        content: idx + 1,
                        selected: idx === (this.selected - 1)
                    };

                    pages[idx] = page;
                }

                // Small util to set a break between pages
                const setPageBreak = idx => {
                    const pageBreak = {
                        disabled: true,
                        pageBreak: true
                    }

                    pages[idx] = pageBreak;
                }

                // Loop low end of limit pages
                for(let i = 0; i < this.limitPages; i++) {
                    setPage(i);
                }

                // Loop selected range
                let lowRange = 0;
                if(this.selected - halvedRange > 0) {
                    lowRange = this.selected - 1 - halvedRange;
                }

                let highRange = lowRange + this.pageRange - 1;
                if(highRange >= this.pageCount) {
                    highRange = this.pageCount - 1;
                    lowRange = highRange - this.pageRange + 1;
                }

                for(let i = lowRange; i <= highRange && i <= this.pageCount - 1; i++) {
                    setPage(i);
                }

                // Check if there is a page break in the left of the range
                if(lowRange > this.limitPages) {
                    setPageBreak(lowRange - 1);
                }

                // Check if there is a page break in the right of the range
                if(highRange + 1 < this.pageCount - this.limitPages) {
                    setPageBreak(highRange + 1);
                }

                // loop high end of limit pages
                for(let i = this.pageCount - 1; i >= this.pageCount - this.limitPages; i--) {
                    setPage(i);
                }
            }
            return pages;
        }
    },
    methods: {
        handlePageSelected(pageSelected) {
            if(this.selected === pageSelected) return;

            this.internalValue = pageSelected;
            this.$emit('input', pageSelected);
        },
        prevPage() {
            if(this.selected <= 1) return;

            this.handlePageSelected(this.selected - 1);
        },
        nextPage() {
            if(this.selected >= this.pageCount) return;

            this.handlePageSelected(this.selected + 1);
        },
        firstPageSelected() {
            return this.selected === 1;
        },
        lastPageSelected() {
            return (this.selected === this.pageCount) || (this.pageCount === 0);
        },
        selectFirstPage()  {
            if(this.selected <= 1) return;
            this.handlePageSelected(1);
        },
        selectLastPage() {
            if(this.selected >= this.pageCount) return

            this.handlePageSelected(this.pageCount);
        }
    },
    watch: {
        value() {
            this.internalValue = this.value
        }
    }
}
</script>

<style lang="scss" scoped>
@import '../../../sass/_variables.scss';

.pagination {
    display: flex;
    list-style: none;
    border-radius: .25rem;
    margin-top: $s-xs;
    margin-bottom: $s-xs;

    .page-item {
        position: relative;
        display: block;
        padding: $s-xs;
        margin-left: -1px;
        background-color: var(--c-panel-bg);
        border: 1px solid var(--c-brand-default-bg);

        .page-link {
            padding: $s-xs;
            color: var(--c-txt);
            cursor: pointer;
            display: block;

            &:hover {
                text-decoration: none;
                background-color: rgba(var(--c-nav-prime-bg-active), 0.1);
            }
        }

        &:first-child {
            margin-left: 0;
            border-top-left-radius: $s-xs;
            border-bottom-left-radius: $s-xs;
        }

        &.disabled {
            cursor: not-allowed;
            opacity: .65;
            filter: grayscale(1);

            .page-link {
                cursor: not-allowed;
                pointer-events: none;

                &:hover {
                    background-color: transparent;
                }
            }
        }

        &.active {
            z-index: 1;
            background-color: var(--c-brand-main);
            border-color: var(--c-brand-main);

            .page-link {
                color: var(--c-brand-primary-fg);
                cursor: default;
            }
        }
    }
}
</style>